import {
  FeatureToggleApiModel,
  FeatureToggleApiResponse,
  FeatureToggleCountryStatus,
  FeatureToggleStatus,
  FeatureToggleStatusItem
} from "../interfaces/api/feature-toggle-api.interface";
import {FeatureToggleModel} from "../interfaces/models/feature-toggle/feature-toggle.model";
import {ToggleState, ToggleType} from "../enums";

export const mapFeatureToggleFromApiResponse = (apiResponse: FeatureToggleApiResponse): FeatureToggleModel[] => {
  if (!apiResponse?.payload?.features) {
    return [];
  }

  return Object.values(apiResponse.payload.features).map(apiFeature => mapFeatureToggleFromApi(apiFeature));
};

export const mapFeatureToggleFromApi = (apiFeature: FeatureToggleApiModel): FeatureToggleModel => {
  return {
    id: apiFeature.id,
    displayName: apiFeature.display_name,
    description: apiFeature.description,
    owner: apiFeature.owner,
    additionalInformation: apiFeature.additional_information || "",
    recommendedState: apiFeature.recommended,
    type: apiFeature.type === "global" ? ToggleType.GLOBAL : ToggleType.CUSTOMER_COUNTRY,
    mytCms: extractStatusValue(apiFeature.status, "trumpf:cms", apiFeature.type),
    mytLive: extractStatusValue(apiFeature.status, "trumpf:live", apiFeature.type),
    mytLiveChina: extractStatusValue(apiFeature.status, "trumpf:live:zh_CN", apiFeature.type),
    application: apiFeature.application,
    affects: apiFeature.affects,
    icon: apiFeature.icon
  };
};

export const mapFeatureToggleToApi = (model: FeatureToggleModel): Partial<FeatureToggleApiModel> => {
  return {
    id: model.id,
    display_name: model.displayName,
    description: model.description,
    owner: model.owner,
    additional_information: model.additionalInformation,
    recommended: model.recommendedState,
    type: model.type === ToggleType.GLOBAL ? "global" : "customerCountry",
    application: model.application || "",
    affects: model.affects,
    icon: model.icon
  };
};

const extractStatusValue = (
  status: FeatureToggleStatus,
  subscriberKey: string,
  type: "global" | "customerCountry"
): boolean | ToggleState.ERROR | null => {
  if (type === "global") {
    if (status[subscriberKey]) {
      const statusItem = status[subscriberKey] as FeatureToggleStatusItem;
      if (statusItem.enabled === null && statusItem.message) {
        return ToggleState.ERROR;
      }
      return statusItem.enabled;
    }
    return null;
  } else {
    return aggregateCountryStatus(status, subscriberKey);
  }
};

const aggregateCountryStatus = (status: FeatureToggleStatus, subscriberKey: string): boolean | ToggleState.ERROR | null => {
  if (!status || typeof status !== "object") {
    return null;
  }

  const countryStatuses: (boolean | null)[] = [];
  let hasError = false;

  Object.keys(status).forEach(countryCode => {
    const countryStatus = status[countryCode] as FeatureToggleCountryStatus | undefined;

    if (countryStatus && typeof countryStatus === "object") {
      const statusItem = countryStatus[subscriberKey as keyof FeatureToggleCountryStatus];
      if (!statusItem) {
        return;
      }
      if (statusItem.enabled === null && statusItem.message) {
        hasError = true;
      } else {
        countryStatuses.push(statusItem.enabled);
      }
    }
  });

  if (hasError) {
    return ToggleState.ERROR;
  }

  if (countryStatuses.length === 0) {
    return null;
  }

  const trueCount = countryStatuses.filter(s => s).length;
  const falseCount = countryStatuses.filter(s => s === false).length;
  const totalCount = countryStatuses.length;

  if (trueCount === totalCount) {
    return true;
  } else if (falseCount === totalCount) {
    return false;
  } else {
    // Mixed states - return null to indicate partial state
    return null;
  }
};
